// React Query + Firestore onSnapshot — keeps mounted screens live without polling.
import {
  getDoc,
  getDocs,
  onSnapshot,
  type DocumentReference,
  type Query,
  type QuerySnapshot
} from 'firebase/firestore';
import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

export const FIVE_MIN = 5 * 60 * 1000;
/** Poll interval for live (in-play) data so the board ticks without a refresh. */
export const LIVE_POLL = 30 * 1000;
export const LIVE_POLL_FAST = 20 * 1000;

/** Live Firestore document — pushes every change into the React Query cache. */
export function useLiveDoc<T>(key: QueryKey, ref: DocumentReference | null, enabled: boolean) {
  const qc = useQueryClient();
  const keyStr = JSON.stringify(key);
  useEffect(() => {
    if (!enabled || !ref) return;
    const unsub = onSnapshot(
      ref,
      (snap) => qc.setQueryData<T | null>(key, snap.exists() ? (snap.data() as T) : null),
      (err) => console.error('[live] doc listener failed', err)
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyStr, enabled, ref?.path]);

  return useQuery<T | null>({
    queryKey: key,
    enabled: enabled && !!ref,
    staleTime: Infinity,
    queryFn: async () => {
      const snap = await getDoc(ref!);
      return snap.exists() ? (snap.data() as T) : null;
    }
  });
}

/** Live Firestore query — optional subscribe mode for fixture/ group streams. */
export function useLiveQuerySnap<R>(
  key: QueryKey,
  q: Query | null,
  enabled: boolean,
  subscribe: boolean,
  transform: (snap: QuerySnapshot) => R
) {
  const qc = useQueryClient();
  const keyStr = JSON.stringify(key);
  useEffect(() => {
    if (!enabled || !subscribe || !q) return;
    const unsub = onSnapshot(
      q,
      (snap) => qc.setQueryData<R>(key, transform(snap)),
      (err) => console.error('[live] query listener failed', err)
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyStr, enabled, subscribe]);

  return useQuery<R>({
    queryKey: key,
    enabled: enabled && !!q,
    staleTime: subscribe ? Infinity : 0,
    queryFn: async () => transform(await getDocs(q!))
  });
}
